import { platformStorageAttachedObjectReferenceSchema } from '@crest/platform-contracts/storage';
import { z } from 'zod';
import { insuranceClaimsCommandReasonSchema, insuranceClaimsCommandReceiptSchema, insuranceClaimsIdSchema, insuranceClaimsMoneyCentsSchema, insuranceClaimsPageSchema, } from './insurance-claims-claim.contract.js';
export const insuranceClaimsSupplementsCapability = 'insurance-claims.supplements.v1';
export const insuranceClaimsSupplementVersionsCapability = 'insurance-claims.supplement-versions.v1';
export const insuranceClaimsSupplementStatusSchema = z.enum([
    'draft', 'submitted', 'approved', 'rejected', 'revised', 'paid',
]);
export const insuranceClaimsSupplementReasonCategorySchema = z.enum([
    'hidden_damage',
    'scope_change',
    'code_upgrade',
    'material_upgrade',
    'mitigation_extension',
    'other',
]);
export const insuranceClaimsQuantityTenThousandthsSchema = z.number().int().min(1).max(10_000_000);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
export const insuranceClaimsSupplementLineInputSchema = z.strictObject({
    lineKey: insuranceClaimsIdSchema,
    sourceReference: z.strictObject({
        capability: z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/),
        sourceId: z.string().trim().min(1).max(240),
        label: z.string().trim().min(1).max(240),
    }).nullable(),
    description: z.string().trim().min(1).max(2_000),
    quantityTenThousandths: insuranceClaimsQuantityTenThousandthsSchema,
    unit: z.string().trim().min(1).max(60),
    unitPriceCents: insuranceClaimsMoneyCentsSchema,
});
export const insuranceClaimsSupplementLineSchema = insuranceClaimsSupplementLineInputSchema.extend({
    lineNumber: z.number().int().positive(),
    requestedAmountCents: insuranceClaimsMoneyCentsSchema,
});
export const insuranceClaimsSupplementDraftSchema = z.strictObject({
    claimId: insuranceClaimsIdSchema,
    title: z.string().trim().min(1).max(240),
    description: nullableText(8_000),
    reasonCategory: insuranceClaimsSupplementReasonCategorySchema,
    currencyCode: z.string().regex(/^[A-Z]{3}$/),
    lines: z.array(insuranceClaimsSupplementLineInputSchema).min(1).max(200),
    evidenceReferences: z.array(platformStorageAttachedObjectReferenceSchema).max(50),
});
export const insuranceClaimsCreateSupplementCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: insuranceClaimsIdSchema,
    supplementId: insuranceClaimsIdSchema,
    supplement: insuranceClaimsSupplementDraftSchema,
    reason: insuranceClaimsCommandReasonSchema,
});
export const insuranceClaimsReviseSupplementCommandSchema = z.strictObject({
    kind: z.literal('revise'),
    commandId: insuranceClaimsIdSchema,
    expectedRevision: z.number().int().positive(),
    supplement: insuranceClaimsSupplementDraftSchema,
    reason: insuranceClaimsCommandReasonSchema,
});
const lifecycleBase = {
    commandId: insuranceClaimsIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: insuranceClaimsCommandReasonSchema,
};
export const insuranceClaimsSubmitSupplementCommandSchema = z.strictObject({
    kind: z.literal('submit'),
    ...lifecycleBase,
});
export const insuranceClaimsApproveSupplementCommandSchema = z.strictObject({
    kind: z.literal('approve'),
    ...lifecycleBase,
    approvedAmountCents: insuranceClaimsMoneyCentsSchema,
    adjusterNotes: nullableText(4_000),
});
export const insuranceClaimsRejectSupplementCommandSchema = z.strictObject({
    kind: z.literal('reject'),
    ...lifecycleBase,
    rejectionReason: z.string().trim().min(1).max(4_000),
});
export const insuranceClaimsRequestRevisionSupplementCommandSchema = z.strictObject({
    kind: z.literal('request-revision'),
    ...lifecycleBase,
    adjusterNotes: z.string().trim().min(1).max(4_000),
});
export const insuranceClaimsSupplementStateCommandSchema = z.strictObject({
    kind: z.enum(['acknowledge-paid', 'archive', 'restore']),
    ...lifecycleBase,
});
export const insuranceClaimsSupplementCommandSchema = z.discriminatedUnion('kind', [
    insuranceClaimsReviseSupplementCommandSchema,
    insuranceClaimsSubmitSupplementCommandSchema,
    insuranceClaimsApproveSupplementCommandSchema,
    insuranceClaimsRejectSupplementCommandSchema,
    insuranceClaimsRequestRevisionSupplementCommandSchema,
    insuranceClaimsSupplementStateCommandSchema,
]);
export const insuranceClaimsSupplementVersionSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsSupplementVersionsCapability),
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    supplementId: insuranceClaimsIdSchema,
    versionNumber: z.number().int().positive(),
    status: insuranceClaimsSupplementStatusSchema,
    requestedAmountCents: insuranceClaimsMoneyCentsSchema,
    approvedAmountCents: insuranceClaimsMoneyCentsSchema.nullable(),
    adjusterNotes: nullableText(4_000),
    rejectionReason: nullableText(4_000),
    contentHash: z.string().regex(/^[a-f0-9]{64}$/),
    reason: insuranceClaimsCommandReasonSchema,
    actorMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    data: insuranceClaimsSupplementDraftSchema.extend({
        lines: z.array(insuranceClaimsSupplementLineSchema),
    }),
});
export const insuranceClaimsSupplementSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsSupplementsCapability),
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    supplementNumber: z.string().trim().min(1).max(80),
    claimId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
    status: insuranceClaimsSupplementStatusSchema,
    revision: z.number().int().positive(),
    currentVersionId: insuranceClaimsIdSchema,
    requestedAmountCents: insuranceClaimsMoneyCentsSchema,
    approvedAmountCents: insuranceClaimsMoneyCentsSchema.nullable(),
    submittedAt: z.string().datetime({ offset: true }).nullable(),
    respondedAt: z.string().datetime({ offset: true }).nullable(),
    paidAt: z.string().datetime({ offset: true }).nullable(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: insuranceClaimsIdSchema,
    updatedByMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: insuranceClaimsSupplementVersionSchema,
});
export const insuranceClaimsSupplementListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    claimId: insuranceClaimsIdSchema.nullable().default(null),
    projectId: insuranceClaimsIdSchema.nullable().default(null),
    status: insuranceClaimsSupplementStatusSchema.nullable().default(null),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const insuranceClaimsSupplementResponseSchema = z.strictObject({
    data: insuranceClaimsSupplementSchema,
    command: insuranceClaimsCommandReceiptSchema.optional(),
});
export const insuranceClaimsSupplementsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsSupplementSchema),
    page: insuranceClaimsPageSchema,
});
export const insuranceClaimsSupplementVersionsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsSupplementVersionSchema),
});
export const insuranceClaimsSupplementSummarySchema = z.strictObject({
    contractVersion: z.literal('insurance-claims.supplement-summaries.v1'),
    claimId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
    currencyCode: z.string().regex(/^[A-Z]{3}$/).nullable(),
    supplementCount: z.number().int().nonnegative(),
    byStatus: z.record(insuranceClaimsSupplementStatusSchema, z.number().int().nonnegative()),
    requestedAmountCents: insuranceClaimsMoneyCentsSchema.nullable(),
    pendingRequestedAmountCents: insuranceClaimsMoneyCentsSchema.nullable(),
    approvedAmountCents: insuranceClaimsMoneyCentsSchema.nullable(),
    approvedUnpaidAmountCents: insuranceClaimsMoneyCentsSchema.nullable(),
    averageResponseHours: z.number().nonnegative().nullable(),
    aging: z.strictObject({
        under48Hours: z.number().int().nonnegative(),
        from48To95Hours: z.number().int().nonnegative(),
        from96To167Hours: z.number().int().nonnegative(),
        atLeast168Hours: z.number().int().nonnegative(),
    }),
});
export function calculateInsuranceClaimsSupplementLineAmount(input) {
    const amount = (BigInt(input.quantityTenThousandths) * BigInt(input.unitPriceCents) + 5000n) / 10000n;
    return insuranceClaimsMoneyCentsSchema.parse(Number(amount));
}
export function calculateInsuranceClaimsSupplementRequestedAmount(lines) {
    return insuranceClaimsMoneyCentsSchema.parse(lines.reduce((sum, line) => sum + calculateInsuranceClaimsSupplementLineAmount(line), 0));
}
//# sourceMappingURL=insurance-claims-supplement.contract.js.map